import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowUp, Loader2, Mic, PanelRightClose, PanelRightOpen, Plus, Square } from "lucide-react";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { ConversationTurn } from "@/components/conversation-turn";
import { FeedbackControl } from "@/components/feedback";
import { Spinner } from "@/components/spinner";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { tools } from "@/lib/tools";
import { useDictation } from "@/lib/use-dictation";
import { cn } from "@/lib/utils";
import { conversationCards } from "@/plugins";

export const Route = createFileRoute("/_authed/conversations/$conversationId")({
  component: Conversation,
});
/**
 * Wide enough for the case as the page and the chat docked beside it, as
 * Tailwind's `lg` and the `docked` variant (`styles.css`). Below it the case
 * sits under the latest reply instead. Read from JS, not CSS alone: the
 * card is mounted in one place or the other, since two mounted copies would
 * each fetch the case and keep their own step and selection.
 */
const WIDE = "(min-width: 1024px)";
function subscribeWide(onChange: () => void) {
  const media = window.matchMedia(WIDE);
  media.addEventListener("change", onChange);
  return () => media.removeEventListener("change", onChange);
}
const isWide = () => window.matchMedia(WIDE).matches;

function Conversation() {
  const { conversationId } = Route.useParams();
  const client = useQueryClient();
  const wide = useSyncExternalStore(subscribeWide, isWide);
  const [draft, setDraft] = useState("");
  // Whether the chat docked beside the case is open, or folded to a rail with its button.
  const [chatOpen, setChatOpen] = useState(true);
  const request = useRef({ prompt: "", id: crypto.randomUUID() });
  const query = useQuery({
    ...tools.queryOptions("GET /api/conversations/{id}", { id: conversationId }),
    refetchInterval: (query) =>
      query.state.data?.runs.some((run) => run.status === "queued" || run.status === "running")
        ? 2000
        : false,
  });
  const active = query.data?.runs.find(
    (run) => run.status === "queued" || run.status === "running",
  );
  const send = useMutation(
    tools.mutationOptions("POST /api/conversations/{id}/messages", {
      onSuccess: async () => {
        setDraft("");
        request.current = { prompt: "", id: crypto.randomUUID() };
        await query.refetch();
        await client.invalidateQueries({ queryKey: tools.queryKey("GET /api/conversations") });
      },
    }),
  );
  // Land on the latest turn when the conversation opens, and on each new turn
  // after sending: its start, as the case below a reply is longer than a
  // screen. Keyed on the turn count, not on streamed text, so reading an
  // earlier answer while one streams is not yanked away.
  const turnCount = query.data?.runs.length ?? 0;
  const latestRef = useRef<HTMLDivElement>(null);
  // A new turn opens a folded chat: it was sent from the composer or the case, to be read.
  useEffect(() => {
    if (turnCount === 0) return;
    setChatOpen(true);
    requestAnimationFrame(() => latestRef.current?.scrollIntoView({ block: "start" }));
  }, [turnCount]);
  const dictation = useDictation((text) =>
    setDraft((prev) => (prev.trim() ? `${prev.trim()} ${text}` : text)),
  );

  if (query.error) return <p role="alert">{query.error.message}</p>;
  if (!query.data) return <Spinner />;
  const data = query.data;
  const locked = Boolean(active) || send.isPending || !data.canContinue;
  // The card belongs to the latest finished reply, and stays below it while the next one runs.
  const answered = data.runs.findLast((run) => run.status !== "queued" && run.status !== "running");
  const said = data.messages.flatMap((message) =>
    message.payload.role === "user" && typeof message.payload.content === "string"
      ? [message.payload.content]
      : [],
  );
  const ratingOf = (runId: string, target: "reply" | "case") =>
    data.feedback.find((f) => f.runId === runId && f.target === target);
  // Keyed by the reply: each reply gets a fresh card, and only the latest has one.
  const cards =
    answered && data.canContinue
      ? conversationCards.map(({ key, Card }) => (
          <Card
            key={`${key}-${answered.id}`}
            conversationId={conversationId}
            busy={Boolean(active) || send.isPending}
            said={said}
            runId={answered.id}
            rated={ratingOf(answered.id, "case")}
            say={(prompt) =>
              send.mutate({
                id: conversationId,
                prompt,
                idempotencyKey: crypto.randomUUID(),
              })
            }
          />
        ))
      : null;
  return (
    <div className="flex w-full flex-1 justify-center gap-6 px-4 md:px-8">
      {/* The case as the page, the window its scroller, so its header pins at the
          top. First, as the chat's classes key on it (`docked`) and it is what
          the page is about; `empty:hidden` gives the chat the page back while no
          card renders. It takes the width the docked chat leaves. */}
      {wide && (
        <aside
          id="fall"
          aria-label="Fall"
          className="w-full min-w-0 max-w-[64rem] flex-1 pb-8 empty:hidden [&>*]:mt-4 md:[&>*]:mt-8"
        >
          {cards}
        </aside>
      )}
      {/* Docked, not floating: a chat over the case covered its right columns,
          the amounts and bonus levels (chat-design-handoff.md § 15). Sticky in
          the window's scroll, as tall as the screen, its turns scrolling inside. */}
      <div
        className={cn(
          "flex w-full min-w-0 max-w-3xl flex-1 flex-col",
          "docked:sticky docked:top-4 docked:mt-4 docked:h-[calc(100dvh-2rem)] docked:flex-none docked:self-start docked:rounded-2xl docked:border docked:bg-background md:docked:top-8 md:docked:mt-8 md:docked:h-[calc(100dvh-4rem)]",
          chatOpen ? "docked:w-[22rem] docked:px-4" : "docked:w-12 docked:px-1",
        )}
      >
        <header className="flex items-start gap-2 pt-4 md:pt-8 docked:pt-3">
          <div className={cn("min-w-0 flex-1 space-y-1", !chatOpen && "docked:hidden")}>
            <Link
              className="inline-flex items-center gap-1 text-muted-foreground text-sm hover:text-foreground"
              to="/agents"
            >
              <Plus aria-hidden className="size-3.5" />
              Neue Unterhaltung
            </Link>
            <h1 className="break-words font-semibold text-2xl tracking-tight docked:truncate docked:text-base">
              {data.title}
            </h1>
          </div>
          <Button
            variant="ghost"
            size="icon"
            aria-expanded={chatOpen}
            aria-controls="verlauf"
            aria-label={chatOpen ? "Unterhaltung einklappen" : "Unterhaltung zeigen"}
            className="hidden shrink-0 docked:inline-flex"
            onClick={() => setChatOpen((open) => !open)}
          >
            {chatOpen ? (
              <PanelRightClose aria-hidden className="size-4" />
            ) : (
              <PanelRightOpen aria-hidden className="size-4" />
            )}
          </Button>
        </header>
        {/* `relative`: the turns hold `sr-only` text, which is `position: absolute`.
            Without a positioned ancestor here its containing block is the page, so
            this scroller neither clips nor scrolls it: each one sits at its place
            in the full, unscrolled history and stretches the window's scroll height
            far below the case, and the sticky sidebar scrolls away with it. */}
        <div
          id="verlauf"
          className={cn(
            "relative flex-1 space-y-10 py-8 docked:min-h-0 docked:overflow-y-auto docked:py-4",
            !chatOpen && "docked:hidden",
          )}
        >
          {data.runs.map((run, i) => (
            <div
              key={run.id}
              ref={i === data.runs.length - 1 ? latestRef : undefined}
              className="scroll-mt-4 space-y-6"
            >
              <ConversationTurn
                run={run}
                messages={data.messages
                  .filter((message) => message.runId === run.id)
                  .map((message) => ({ ...message.payload, messageIndex: message.messageIndex }))}
                onComplete={async () => {
                  await query.refetch();
                  await client.invalidateQueries({
                    queryKey: tools.queryKey("GET /api/conversations"),
                  });
                  await client.invalidateQueries({ queryKey: tools.queryKey("GET /api/me") });
                }}
              />
              {/* Outside the turn, so the turn stays a view of the run; before the
                  card, as it rates the reply above, not the case below. */}
              {(run.status === "completed" || run.status === "failed") && (
                <div className="-mt-4">
                  <FeedbackControl
                    conversationId={conversationId}
                    runId={run.id}
                    target="reply"
                    current={ratingOf(run.id, "reply")}
                    question="War diese Antwort hilfreich?"
                  />
                </div>
              )}
              {!wide && run.id === answered?.id && cards}
            </div>
          ))}
          {!data.canContinue && (
            <p className="rounded-lg bg-muted p-4 text-sm">
              Diese Unterhaltung wurde mit einer anderen Modellkonfiguration begonnen und kann nicht
              fortgesetzt werden.{" "}
              <Link className="underline decoration-accent underline-offset-4" to="/agents">
                Neue Unterhaltung starten
              </Link>
            </p>
          )}
        </div>
        <form
          // The fade lets the case scroll under the composer instead of being cut
          // off at a hard edge; it takes no clicks, so the case stays usable to it.
          className={cn(
            "group sticky bottom-0 bg-background pt-2 pb-4 before:pointer-events-none before:absolute before:inset-x-0 before:-top-8 before:h-8 before:bg-gradient-to-t before:from-background md:pb-6 docked:rounded-b-2xl docked:pb-3 docked:before:hidden",
            // Folded to a rail, the chat is its button alone.
            !chatOpen && "docked:hidden",
          )}
          onSubmit={(event) => {
            event.preventDefault();
            // Enter submits via requestSubmit, which ignores the disabled
            // button, so the guard has to live here rather than on the button.
            if (!draft.trim() || locked) return;
            if (request.current.prompt !== draft)
              request.current = { prompt: draft, id: crypto.randomUUID() };
            send.mutate({ id: conversationId, prompt: draft, idempotencyKey: request.current.id });
          }}
        >
          {(send.error || dictation.error) && (
            <p role="alert" className="mb-2 text-destructive text-sm">
              {send.error?.message ?? dictation.error}
            </p>
          )}
          <div className="flex items-end gap-2 rounded-xl border bg-background p-2 transition-colors focus-within:border-accent focus-within:ring-2 focus-within:ring-accent/25">
            <label htmlFor="follow-up" className="sr-only">
              Folgefrage
            </label>
            <Textarea
              id="follow-up"
              submitOnEnter
              rows={1}
              className="min-h-10 border-0 px-2 py-2 focus-visible:ring-0"
              placeholder={
                dictation.state === "recording"
                  ? "Aufnahme läuft…"
                  : dictation.state === "transcribing"
                    ? "Wird erkannt…"
                    : active
                      ? "mola antwortet…"
                      : "Folgefrage stellen…"
              }
              value={draft}
              onChange={(event) => setDraft(event.target.value)}
              disabled={locked || dictation.state !== "idle"}
            />
            {dictation.supported && (
              <Button
                type="button"
                size="icon"
                variant={dictation.state === "recording" ? "destructive" : "outline"}
                aria-label={
                  dictation.state === "recording"
                    ? "Aufnahme beenden"
                    : dictation.state === "transcribing"
                      ? "Wird erkannt"
                      : "Diktieren"
                }
                aria-pressed={dictation.state === "recording"}
                className="size-10 shrink-0 rounded-lg"
                disabled={locked || dictation.state === "transcribing"}
                onClick={dictation.toggle}
              >
                {dictation.state === "transcribing" ? (
                  <Loader2 className="size-4 animate-spin" />
                ) : dictation.state === "recording" ? (
                  <Square className="size-3.5 fill-current" />
                ) : (
                  <Mic className="size-4" />
                )}
              </Button>
            )}
            <Button
              size="icon"
              aria-label="Senden"
              className="size-10 shrink-0 rounded-lg"
              disabled={!draft.trim() || locked || dictation.state !== "idle"}
            >
              <ArrowUp className="size-4" />
            </Button>
          </div>
          {/* Only while writing: otherwise a line under every view that says nothing new. */}
          <p className="mt-1.5 hidden text-center text-muted-foreground text-xs md:group-focus-within:block">
            Enter zum Senden · Umschalt+Enter für eine neue Zeile
          </p>
        </form>
      </div>
    </div>
  );
}
